/**
 * Documents – store receipts, payslips, IDs, insurance, contracts, etc.
 * Wired to GET/POST/PUT/DELETE /api/v1/documents and POST /api/v1/documents/scan.
 */
import React, { useEffect, useState } from 'react';
import { documentsApi, type DocumentItem } from '../services/api';

const CATEGORIES = [
  'receipt',
  'payslip',
  'identity',
  'insurance',
  'contract',
  'certificate',
  'medical',
  'tax',
  'education',
  'other',
];

const Documents: React.FC = () => {
  const [items, setItems] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeCategory, setActiveCategory] = useState<string>('');

  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('receipt');
  const [newDescription, setNewDescription] = useState('');
  const [newMediaUrl, setNewMediaUrl] = useState('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('receipt');
  const [editDescription, setEditDescription] = useState('');
  const [editMediaUrl, setEditMediaUrl] = useState('');

  const [showScan, setShowScan] = useState(false);
  const [scanName, setScanName] = useState('');
  const [scanCategory, setScanCategory] = useState('receipt');
  const [scanSourceImageUrl, setScanSourceImageUrl] = useState('');
  const [scanning, setScanning] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadItems = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await documentsApi.list(activeCategory || undefined);
      if (res?.success) {
        setItems(Array.isArray(res?.data) ? res.data : []);
      } else setError(res?.error || 'Failed to load documents');
    } catch {
      setError('Failed to load documents');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await documentsApi.create({
        name: newName.trim(),
        category: newCategory,
        description: newDescription.trim() || undefined,
        mediaUrl: newMediaUrl.trim() || undefined,
      });
      if (res?.success) {
        setNewName('');
        setNewDescription('');
        setNewMediaUrl('');
        setShowAdd(false);
        loadItems(true);
      } else setError(res?.error || 'Failed to add document');
    } catch {
      setError('Failed to add document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item: DocumentItem) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditCategory(item.category || 'other');
    setEditDescription(item.description || '');
    setEditMediaUrl(item.mediaUrl || '');
  };

  const handleUpdate = async () => {
    if (!editingId || !editName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await documentsApi.update(editingId, {
        name: editName.trim(),
        category: editCategory,
        description: editDescription.trim() || undefined,
        mediaUrl: editMediaUrl.trim() || undefined,
      });
      if (res?.success) {
        setEditingId(null);
        loadItems(true);
      } else setError(res?.error || 'Failed to update');
    } catch {
      setError('Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    setError(null);
    try {
      const res = await documentsApi.delete(id);
      if (res?.success) loadItems(true);
      else setError(res?.error || 'Failed to delete');
    } catch {
      setError('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const handleScan = async () => {
    if (!scanSourceImageUrl.trim()) return;
    setScanning(true);
    setError(null);
    try {
      const res = await documentsApi.scan({
        name: scanName.trim() || undefined,
        category: scanCategory,
        sourceImageUrl: scanSourceImageUrl.trim(),
      });
      if (res?.success) {
        setScanName('');
        setScanSourceImageUrl('');
        setShowScan(false);
        loadItems(true);
      } else setError(res?.error || 'Failed to scan document');
    } catch {
      setError('Failed to scan document');
    } finally {
      setScanning(false);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>📄 Documents</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
              Store receipts, payslips, IDs, insurance, contracts, and more
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setShowAdd((s) => !s)}
              style={{
                padding: '8px 16px',
                background: 'rgba(76,175,80,0.9)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Add Document
            </button>
            <button
              type="button"
              onClick={() => loadItems(true)}
              disabled={refreshing}
              style={{
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '8px',
                color: 'white',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <button
          type="button"
          onClick={() => setActiveCategory('')}
          style={{
            padding: '6px 14px',
            background: activeCategory === '' ? '#3b82f6' : '#f1f5f9',
            color: activeCategory === '' ? 'white' : '#334155',
            border: 'none',
            borderRadius: '999px',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          All
        </button>
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setActiveCategory(cat)}
            style={{
              padding: '6px 14px',
              background: activeCategory === cat ? '#3b82f6' : '#f1f5f9',
              color: activeCategory === cat ? 'white' : '#334155',
              border: 'none',
              borderRadius: '999px',
              cursor: 'pointer',
              fontSize: '13px',
              textTransform: 'capitalize',
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {showAdd && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid #e2e8f0',
        }}>
          <input
            type="text"
            placeholder="Document name (e.g. Grocery receipt)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
              textTransform: 'capitalize',
            }}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Description (optional)"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <input
            type="text"
            placeholder="Media URL (optional, link to a hosted file/image)"
            value={newMediaUrl}
            onChange={(e) => setNewMediaUrl(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleAdd}
              disabled={submitting || !newName.trim()}
              style={{
                padding: '8px 16px',
                background: '#16a34a',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {submitting ? 'Adding…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setShowAdd(false); setNewName(''); setNewDescription(''); setNewMediaUrl(''); }}
              style={{
                padding: '8px 16px',
                background: '#64748b',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {editingId && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          border: '2px solid #3b82f6',
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Edit document</h3>
          <input
            type="text"
            placeholder="Document name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <select
            value={editCategory}
            onChange={(e) => setEditCategory(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
              textTransform: 'capitalize',
            }}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Description (optional)"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <input
            type="text"
            placeholder="Media URL (optional)"
            value={editMediaUrl}
            onChange={(e) => setEditMediaUrl(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleUpdate}
              disabled={submitting || !editName.trim()}
              style={{
                padding: '8px 16px',
                background: '#3b82f6',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setEditingId(null); setEditName(''); setEditDescription(''); setEditMediaUrl(''); }}
              style={{
                padding: '8px 16px',
                background: '#64748b',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{
        background: '#eff6ff',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '16px',
        border: '1px solid #bfdbfe',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <strong style={{ fontSize: '14px' }}>📷 Scan a document</strong>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#475569' }}>
              Soon you'll be able to snap a photo with your phone camera. For now, paste a hosted image URL below and we'll run it through OCR.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowScan((s) => !s)}
            style={{
              padding: '8px 16px',
              background: '#3b82f6',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap',
            }}
          >
            Scan
          </button>
        </div>
        {showScan && (
          <div style={{ marginTop: '12px' }}>
            <input
              type="text"
              placeholder="Document name (optional)"
              value={scanName}
              onChange={(e) => setScanName(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                marginBottom: '10px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '14px',
              }}
            />
            <select
              value={scanCategory}
              onChange={(e) => setScanCategory(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                marginBottom: '10px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '14px',
                textTransform: 'capitalize',
              }}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Hosted image URL (required)"
              value={scanSourceImageUrl}
              onChange={(e) => setScanSourceImageUrl(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                marginBottom: '10px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '14px',
              }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleScan}
                disabled={scanning || !scanSourceImageUrl.trim()}
                style={{
                  padding: '8px 16px',
                  background: '#16a34a',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: scanning ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                }}
              >
                {scanning ? 'Scanning…' : 'Submit Scan'}
              </button>
              <button
                type="button"
                onClick={() => { setShowScan(false); setScanName(''); setScanSourceImageUrl(''); }}
                style={{
                  padding: '8px 16px',
                  background: '#64748b',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}
      {items.length === 0 && !error && !showAdd && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          color: '#64748b',
          fontSize: '14px',
        }}>
          No documents yet. Add one above or scan one in.
        </div>
      )}
      {items.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map((item) => (
            <li
              key={item.id}
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div>
                <strong>{item.name}</strong>
                {item.category && (
                  <span style={{
                    marginLeft: '8px',
                    fontSize: '11px',
                    color: '#3b82f6',
                    background: '#eff6ff',
                    padding: '2px 8px',
                    borderRadius: '999px',
                    textTransform: 'capitalize',
                  }}>
                    {item.category}
                  </span>
                )}
                {item.description && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>{item.description}</p>
                )}
                {item.ocrStatus && item.ocrStatus !== 'none' && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>OCR: {item.ocrStatus}</p>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => handleEdit(item)}
                  style={{
                    padding: '6px 12px',
                    background: '#3b82f6',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(item.id)}
                  disabled={deleting === item.id}
                  style={{
                    padding: '6px 12px',
                    background: '#dc2626',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    cursor: deleting === item.id ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                  }}
                >
                  {deleting === item.id ? '…' : 'Delete'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Documents;
